import {toast} from 'react-toastify';
import React, {FC, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {ArrowLeftIcon, CheckIcon} from '@heroicons/react/24/outline';
import {applyDataCatalogUpdate, IDataCatalogChange, IDataCatalogEntryFields, VerdocsEndpoint} from '@verdocs/js-sdk';
import {invalidateDataCatalogs, useDataCatalog, useDataCatalogListing, useDataCatalogUpdate} from '../../react-sdk/queries';
import {Button, SegmentedControl, Spinner} from '../../Components';
import {URLS} from '../../Routing/urls';
import {AppState} from '../../AppState';

type TResolution = 'source' | 'local';

const TYPE_LABELS = {text: 'Text', date: 'Date', boolean: 'Yes/No', choice: 'Choice'};

const describe = (entry?: IDataCatalogEntryFields) => (entry ? `${entry.label} (${TYPE_LABELS[entry.type]})` : '');

// What an incoming version changes about a key, in a few words
const differences = (local?: IDataCatalogEntryFields, source?: IDataCatalogEntryFields) => {
  if (!local || !source) return '';
  const out: string[] = [];
  if (local.label !== source.label) out.push(`Label becomes "${source.label}"`);
  if (local.type !== source.type) out.push(`Type becomes ${TYPE_LABELS[source.type]}`);
  if (JSON.stringify(local.options) !== JSON.stringify(source.options)) out.push('Options change');
  if (local.description !== source.description) out.push('Description changes');
  if (local.example !== source.example) out.push('Example changes');
  if (local.sensitive !== source.sensitive) out.push(source.sensitive ? 'Becomes sensitive' : 'No longer sensitive');
  return out.join(' · ');
};

const usedBy = (change: IDataCatalogChange) =>
  change.templates?.length ? `Used by ${change.templates.map((template) => template.name).join(', ')}` : '';

const Group: FC<{title: string; children: React.ReactNode}> = ({title, children}) => (
  <div className="mb-5">
    <h2 className="mb-1.5 text-sm font-semibold uppercase tracking-wide text-gray-700">{title}</h2>
    {children}
  </div>
);

const ChangeRow: FC<{
  change: IDataCatalogChange;
  detail: string;
  kept: boolean;
  keepLabel: string;
  takeLabel: string;
  onToggle: () => void;
}> = ({change, detail, kept, keepLabel, takeLabel, onToggle}) => (
  <div className="flex items-center gap-3 border-t border-[#f2f3f5] py-2 first:border-0">
    <CheckIcon className={`size-4 shrink-0 ${kept ? 'invisible' : 'text-[#2e9b64]'}`} />
    <span className={`w-[250px] shrink-0 truncate font-mono text-[12.5px] ${kept ? 'text-gray-400 line-through' : 'text-indigo-700'}`}>
      {change.key}
    </span>
    <span className="min-w-0 flex-1 truncate text-[12.5px] text-gray-600">{kept ? 'Staying as it is' : detail}</span>
    <button onClick={onToggle} className="shrink-0 text-[12.5px] font-medium text-indigo-600 hover:underline">
      {kept ? takeLabel : keepLabel}
    </button>
  </div>
);

export const DataCatalogUpdate: FC = () => {
  const navigate = useNavigate();
  const {catalogId = ''} = useParams();
  const organizationId = AppState.currentProfile?.organization_id || '';
  const catalogQuery = useDataCatalog(organizationId, catalogId);
  const updateQuery = useDataCatalogUpdate(organizationId, catalogId);
  const sourceQuery = useDataCatalogListing(organizationId, catalogQuery.data?.source_catalog_id || '');
  const [resolutions, setResolutions] = useState<Record<string, TResolution>>({});
  const [applying, setApplying] = useState(false);

  const catalog = catalogQuery.data;
  const update = updateQuery.data;
  const back = () => navigate(URLS.SETTINGS_DATA_CATALOG.build(catalogId));

  if (!catalog || updateQuery.isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner mode="dark" />
      </div>
    );
  }

  const changes = update?.changes || [];
  const resolution = (key: string): TResolution | undefined => resolutions[key];
  const toggle = (key: string) => setResolutions({...resolutions, [key]: resolution(key) === 'local' ? 'source' : 'local'});
  const conflicts = changes.filter((change) => change.kind === 'conflict');
  const undecided = conflicts.filter((change) => !resolution(change.key)).length;
  const publisher = sourceQuery.data?.publisher.name || 'The publisher';

  const handleApply = () => {
    if (applying || undecided) {
      return;
    }

    setApplying(true);
    applyDataCatalogUpdate(VerdocsEndpoint.getDefault(), organizationId, catalog.id, resolutions)
      .then(() => {
        setApplying(false);
        invalidateDataCatalogs();
        toast.success(`${catalog.name} is now on version ${update?.to_version}`);
        back();
      })
      .catch((e: any) => {
        setApplying(false);
        toast.error(`Error applying the update: ${e.response?.data?.message || e.message}`);
      });
  };

  const rows = (kind: IDataCatalogChange['kind'], detail: (change: IDataCatalogChange) => string, keepLabel: string, takeLabel: string) =>
    changes
      .filter((change) => change.kind === kind)
      .map((change) => (
        <ChangeRow
          key={change.key}
          change={change}
          detail={detail(change)}
          kept={resolution(change.key) === 'local'}
          keepLabel={keepLabel}
          takeLabel={takeLabel}
          onToggle={() => toggle(change.key)}
        />
      ));

  const count = (kind: IDataCatalogChange['kind']) => changes.filter((change) => change.kind === kind).length;

  return (
    <div className="flex flex-1 flex-col box-border overflow-y-scroll">
      <div className="w-full max-w-[900px] pb-10">
        <button onClick={back} className="mb-3 flex items-center gap-1.5 text-[13px] text-indigo-600 hover:underline">
          <ArrowLeftIcon className="size-3.5" />
          {catalog.name}
        </button>

        {!update || changes.length === 0 ? (
          <>
            <h1 className="mb-1 text-2xl text-[#33364b]">No update waiting</h1>
            <p className="text-[13px] text-gray-500">{catalog.name} is on the newest version its publisher released.</p>
          </>
        ) : (
          <>
            <h1 className="mb-1 text-2xl text-[#33364b]">Update to version {update.to_version}</h1>
            <p className="mb-5 text-[13px] text-gray-500">
              {publisher} released version {update.to_version}. Your copy is on version {update.from_version}.
            </p>

            {conflicts.length > 0 && (
              <Group title={`Needs your decision · ${conflicts.length}`}>
                {conflicts.map((change) => (
                  <div key={change.key} className="mb-2 flex items-center gap-4 rounded-lg border-[1.5px] border-red-200 px-3.5 py-2.5">
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-[12.5px] text-indigo-700">{change.key}</div>
                      {usedBy(change) && <div className="mt-0.5 text-[11.5px] text-gray-500">{usedBy(change)}</div>}
                    </div>
                    <div className="w-[190px] text-[12.5px]">
                      <div className="text-[11px] text-gray-400">Yours</div>
                      {describe(change.local)}
                    </div>
                    <div className="w-[190px] text-[12.5px]">
                      <div className="text-[11px] text-gray-400">{publisher === 'The publisher' ? 'Theirs' : `${publisher}'s`}</div>
                      {describe(change.source)}
                    </div>
                    <SegmentedControl
                      options={[
                        {key: 'local', label: 'Keep yours'},
                        {key: 'source', label: 'Take theirs'},
                      ]}
                      value={resolution(change.key) || ''}
                      onChange={(key) => setResolutions({...resolutions, [change.key]: key as TResolution})}
                    />
                  </div>
                ))}
              </Group>
            )}

            {count('updated') > 0 && (
              <Group title={`Will update · ${count('updated')}`}>
                {rows('updated', (change) => differences(change.local, change.source), 'Keep yours', 'Take update')}
              </Group>
            )}

            {count('added') > 0 && (
              <Group title={`Will add · ${count('added')}`}>{rows('added', (change) => describe(change.source), 'Skip', 'Add')}</Group>
            )}

            {count('removed') > 0 && (
              <Group title={`Will remove · ${count('removed')}`}>
                {rows('removed', (change) => usedBy(change) || describe(change.local), 'Keep it', 'Remove')}
              </Group>
            )}

            <div className="mt-2 flex items-center gap-2 border-t border-[#f2f3f5] pt-4">
              {undecided > 0 && (
                <span className="text-[12.5px] text-red-600">
                  {undecided} {undecided === 1 ? 'decision' : 'decisions'} left
                </span>
              )}
              <span className="flex-1" />
              {applying && <Spinner mode="dark" />}
              <Button size="small" variant="secondary" label="Cancel" onClick={back} disabled={applying} />
              <Button size="small" label="Apply update" onClick={handleApply} disabled={applying || undecided > 0} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
